import { createSlice } from '@reduxjs/toolkit'
import {
  addAction,
  addActionsToProject,
  deleteAction,
  fetchActionsByRelatedControlId,
  fetchActionsWithoutProject,
  getAExistingControlbySecurityControl,
  getActionbyAudit,
  getActionbyId,
  getActionbyIncident,
  getActionbyProject,
  getActionbyRisque,
  getActionbySecurityCheckFamily,
  getActions,
  getActionsByMissionId,
  getActionsBySubDomainId,
  getSubDomainbySecurityControl,
  removeActionFromProject,
  updateAction,
} from './actionActions'
import { Action, ActionResponse } from '../../types/actionTypes'
import { ExistingControl } from '../../types/existingControlTypes'
import { SubDomain } from '../../types/subDomainTypes'

interface ActionState {
  actions: Action[]
  actionsWithoutProject: Action[]
  relatedActions: Action[]
  loading: boolean
  action: Action | null
  error: string | null
  addedAction: Action | null
  updatedAction: Action | null
  ActionResponse: ActionResponse | null
  actionsbyAudit: Action[]
  actionsbyIncident: Action[]
  actionsbyRisque: Action[]
  actionsbyproject: ActionResponse | null
  actionsbySecurity: Action[]
  actionsbyMission: Action[]
  existingControls: ExistingControl[]
  subDomains: SubDomain[]
  actionsBySubDomains: Action[]
}

const initialState: ActionState = {
  actions: [],
  actionsWithoutProject: [],
  relatedActions: [],
  loading: false,
  error: null,
  addedAction: null,
  action: null,
  updatedAction: null,
  ActionResponse: null,
  actionsbyAudit: [],
  actionsbyIncident: [],
  actionsbyRisque: [],
  actionsbyproject: null,
  actionsbySecurity: [],
  actionsbyMission: [],
  existingControls: [],
  subDomains: [],
  actionsBySubDomains: [],
}

export const actionSlice = createSlice({
  name: 'action',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getActions.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(getActions.fulfilled, (state, action) => {
        state.loading = false
        state.ActionResponse = {
          actions: action.payload.actions,
          totalPages: action.payload.totalPages ?? 0,
          total: action.payload.total ?? 0,
          status: action.payload.status,
          message: action.payload.message,
        }
      })
      .addCase(getActions.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Something went wrong'
      })
      .addCase(deleteAction.fulfilled, (state, action) => {
        state.loading = false
        const deletedActionId = action.meta.arg

        if (state.ActionResponse) {
          state.ActionResponse.actions = (
            state.ActionResponse.actions ?? []
          ).filter((action) => action._id !== deletedActionId)

          state.ActionResponse.total = state.ActionResponse.total ?? 0
          state.ActionResponse.totalPages = state.ActionResponse.totalPages ?? 0
          state.ActionResponse.currentPage =
            state.ActionResponse.currentPage ?? 0
        }
      })
      .addCase(fetchActionsWithoutProject.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchActionsWithoutProject.fulfilled, (state, action) => {
        state.loading = false
        state.actionsWithoutProject = action.payload
      })
      .addCase(fetchActionsWithoutProject.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload || 'Something went wrong'
      })

      .addCase(fetchActionsByRelatedControlId.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchActionsByRelatedControlId.fulfilled, (state, action) => {
        state.loading = false
        state.relatedActions = action.payload
      })
      .addCase(fetchActionsByRelatedControlId.rejected, (state, action) => {
        state.loading = false
        state.error = (action.payload as string) || 'Failed to fetch actions.'
      })

      .addCase(addAction.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(addAction.fulfilled, (state, action) => {
        state.loading = false
        state.addedAction = action.payload
        console.log(action.payload.SourceIncident)
        state.actions.push(action.payload)
      })
      .addCase(addAction.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Something went wrong'
      })
      .addCase(updateAction.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updateAction.fulfilled, (state, action) => {
        state.loading = false
        state.updatedAction = action.payload
      })
      .addCase(updateAction.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Something went wrong'
      })
      .addCase(getActionbyId.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(getActionbyId.fulfilled, (state, action) => {
        state.loading = false
        state.action = action.payload
      })
      .addCase(getActionbyId.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Something went wrong'
      })
      .addCase(getActionbyAudit.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(getActionbyAudit.fulfilled, (state, action) => {
        state.loading = false
        const actions = Object.values(action.payload).filter(
          (item) => item && typeof item === 'object'
        )
        state.actionsbyAudit = actions as []
      })
      .addCase(getActionbyAudit.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Something went wrong'
      })

      .addCase(getActionbyIncident.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(getActionbyIncident.fulfilled, (state, action) => {
        state.loading = false
        const actions = Object.values(action.payload).filter(
          (item) => item && typeof item === 'object'
        )
        state.actionsbyIncident = actions
      })
      .addCase(getActionbyIncident.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Something went wrong'
      })

      .addCase(getActionbyRisque.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(getActionbyRisque.fulfilled, (state, action) => {
        state.loading = false
        const actions = Object.values(action.payload).filter(
          (item) => item && typeof item === 'object'
        )
        state.actionsbyRisque = actions
      })
      .addCase(getActionbyRisque.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Something went wrong'
      })
      .addCase(getActionbyProject.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(getActionbyProject.fulfilled, (state, action) => {
        state.loading = false
        state.actionsbyproject = action.payload
      })
      .addCase(getActionbyProject.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Something went wrong'
      })
      .addCase(getActionbySecurityCheckFamily.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(getActionbySecurityCheckFamily.fulfilled, (state, action) => {
        state.loading = false
        state.actionsbySecurity = action.payload
      })
      .addCase(getActionbySecurityCheckFamily.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Something went wrong'
      })

      .addCase(getAExistingControlbySecurityControl.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(
        getAExistingControlbySecurityControl.fulfilled,
        (state, action) => {
          state.loading = false
          state.existingControls = action.payload
        }
      )
      .addCase(
        getAExistingControlbySecurityControl.rejected,
        (state, action) => {
          state.loading = false
          state.error = action.error.message || 'Something went wrong'
        }
      )

      .addCase(getSubDomainbySecurityControl.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(getSubDomainbySecurityControl.fulfilled, (state, action) => {
        state.loading = false
        state.subDomains = action.payload
      })
      .addCase(getSubDomainbySecurityControl.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Something went wrong'
      })

      .addCase(removeActionFromProject.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Something went wrong'
      })

      .addCase(removeActionFromProject.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(removeActionFromProject.fulfilled, (state, action) => {
        state.loading = false
        state.actionsbyproject = action.payload
      })
      .addCase(addActionsToProject.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Something went wrong'
      })

      .addCase(addActionsToProject.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(addActionsToProject.fulfilled, (state, action) => {
        state.loading = false
        state.actionsbyproject = action.payload
      })

      .addCase(getActionsByMissionId.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(getActionsByMissionId.fulfilled, (state, action) => {
        state.loading = false
        state.ActionResponse = {
          actions: action.payload.actions,
          totalPages: action.payload.totalPages ?? 0,
          total: action.payload.total ?? 0,
        }
      })
      .addCase(getActionsByMissionId.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Something went wrong'
      })

      .addCase(getActionsBySubDomainId.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(getActionsBySubDomainId.fulfilled, (state, action) => {
        state.loading = false
        state.actionsBySubDomains = action.payload
      })
      .addCase(getActionsBySubDomainId.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Something went wrong'
      })
  },
})

export default actionSlice.reducer
